// Game flow for an online game. There is no rule of its own here: the server
// decides, and every `STATE_SYNC` is adopted as the truth. The one thing done
// locally is playing our own move at once (so the piece does not snap back
// while the echo travels); if the server disagrees, its next snapshot simply
// replaces what we showed. No canvas, no DOM — tests drive it with a fake
// transport.
import { GameState } from '../game/GameState.js';
import { CLOCK_START_PLIES } from '../game/clock.js';
import type { RuleSet } from '../game/rules/index.js';
import { FIDE } from '../game/rules/index.js';
import { opposite, type Color, type Move } from '../game/types.js';
import { moveToUci } from '../game/uci.js';
import {
  stateFromSnapshot, type ChessClockSnapshot, type ChessSnapshot, type ClientMessage,
} from '../network/protocol.js';
import type { Match, MatchMove } from './Match.js';
import { stepSnapshot } from './snapshotStep.js';
import type { ClockReading } from './MatchClock.js';

/** The slice of `NetworkGameController` the match uses. */
export interface ChessTransport {
  readonly localColor: Color | null;
  /** False while our own link is down. */
  readonly playable: boolean;
  attach(): void;
  detach(): void;
  send(message: ClientMessage): void;
}

/** What happened to the draw offer on the table, from our side of the board. */
export type DrawEvent = 'received' | 'withdrawn' | 'declined';

export interface OnlineMatchOptions {
  /** Builds the transport, wiring its inbound snapshots back into this match. */
  readonly connect: (onSnapshot: (snapshot: ChessSnapshot) => void) => ChessTransport;
  readonly onMove: (played: MatchMove) => void;
  readonly onChange: () => void;
  /** A fresh game replaced the one on the board (rematch, agreed restart). */
  readonly onNewGame: () => void;
  readonly onDrawEvent: (event: DrawEvent) => void;
  readonly rules?: RuleSet;
  readonly now?: () => number;
}

export class OnlineMatch implements Match {
  readonly opponent = 'online';
  readonly botThinking = false;
  private readonly transport: ChessTransport;
  private readonly rules: RuleSet;
  private readonly now: () => number;
  private gameState: GameState;
  /** The last clock reading and when it arrived; we count down from there. */
  private clocks: { readonly reading: ChessClockSnapshot; readonly at: number } | null = null;
  private drawOffer: Color | null = null;
  /** The ply at which we last offered a draw — the server allows one per ply. */
  private offeredAtPly: number | null = null;
  /** Our last move, until a snapshot includes it (or overrules it). */
  private ownMove: { readonly ply: number; readonly uci: string } | null = null;
  /** No move is ours to make until the server has said what the position is. */
  private synced = false;

  constructor(private readonly opts: OnlineMatchOptions) {
    this.rules = opts.rules ?? FIDE;
    this.now = opts.now ?? Date.now;
    this.gameState = GameState.start(this.rules);
    this.transport = opts.connect((snapshot) => this.applySnapshot(snapshot));
  }

  get state(): GameState {
    return this.gameState;
  }

  get humanColor(): Color {
    return this.transport.localColor ?? 'w';
  }

  /** The colour with a draw offer on the table, if any. */
  get drawOfferFrom(): Color | null {
    return this.drawOffer;
  }

  /** True once the server has sent the position (false while the room is still filling). */
  get isSynced(): boolean {
    return this.synced;
  }

  start(): void {
    this.transport.attach();
  }

  dispose(): void {
    this.transport.detach();
  }

  private get live(): boolean {
    return this.synced && !this.gameState.isOver && this.transport.playable && this.transport.localColor !== null;
  }

  canHumanMove(): boolean {
    return this.live && this.gameState.turn === this.transport.localColor;
  }

  canOfferDraw(): boolean {
    return this.live && this.drawOffer === null && this.offeredAtPly !== this.gameState.moves.length;
  }

  clock(): ClockReading | null {
    if (this.clocks === null) return null;
    const { reading, at } = this.clocks;
    const running = this.gameState.isOver ? null : reading.running;
    const elapsed = this.now() - at;
    const left = (color: Color): number => (running === color ? Math.max(0, reading[color] - elapsed) : reading[color]);
    return { w: left('w'), b: left('b'), running };
  }

  playHuman(move: Move): boolean {
    if (!this.canHumanMove()) return false;
    const before = this.gameState.position;
    const settled = this.clock();
    this.gameState = this.gameState.play(move);
    this.drawOffer = null;
    if (settled !== null) {
      // Our clock stops now, not when the echo lands; the snapshot corrects any drift.
      const started = this.gameState.moves.length >= CLOCK_START_PLIES && !this.gameState.isOver;
      this.clocks = { reading: { ...settled, running: started ? this.gameState.turn : null }, at: this.now() };
    }
    this.ownMove = { ply: this.gameState.moves.length - 1, uci: moveToUci(move) };
    this.transport.send({ type: 'CHESS_MOVE', uci: this.ownMove.uci });
    this.opts.onMove({ move: this.gameState.lastMove ?? move, before, by: 'human' });
    return true;
  }

  resign(): void {
    if (this.live) this.transport.send({ type: 'RESIGN' });
  }

  offerDraw(): void {
    if (!this.canOfferDraw()) return;
    this.offeredAtPly = this.gameState.moves.length;
    this.drawOffer = this.transport.localColor;
    this.transport.send({ type: 'CHESS_DRAW_OFFER' });
    this.opts.onChange();
  }

  /** Answer the opponent's offer. */
  answerDraw(accept: boolean): void {
    if (!this.live || this.drawOffer !== opposite(this.humanColor)) return;
    if (!accept) this.drawOffer = null;
    this.transport.send({ type: 'CHESS_DRAW_RESPONSE', accept });
    this.opts.onChange();
  }

  private applySnapshot(snapshot: ChessSnapshot): void {
    const prev = this.gameState;
    const stepped = this.synced ? stepSnapshot(prev, snapshot) : null;
    const next = stepped?.state ?? stateFromSnapshot(snapshot, this.rules);
    // Unreadable (a newer server, a corrupted frame): keep what we have.
    if (next === null) return;
    const offerBefore = this.drawOffer;
    const own = this.ownMove;
    // Sent before the server saw our move: it rolls the move back until the echo, nothing more.
    const predatesOwn = own !== null && snapshot.moves.length === own.ply;
    const restarted = stepped === null && next.moves.length === 0 && !predatesOwn;
    const fresh = this.synced && !next.isOver && !predatesOwn && (prev.isOver || restarted);
    // The echo of a move we already showed, landing after such a rollback.
    const ownReplayed = own !== null && own.ply === prev.moves.length && own.uci === snapshot.moves[own.ply];
    // Still awaited only while the game it was played in is the one still going.
    if (!predatesOwn || next.isOver) this.ownMove = null;
    this.gameState = next;
    this.synced = true;
    this.clocks = snapshot.clocks === null ? null : { reading: snapshot.clocks, at: this.now() };
    this.drawOffer = snapshot.drawOfferFrom;
    if (fresh) {
      this.offeredAtPly = null;
      this.opts.onNewGame();
    }
    const move = stepped?.moved === true && !ownReplayed ? next.lastMove : undefined;
    if (move !== undefined) this.opts.onMove({ move, before: prev.position, by: 'remote' });
    else this.opts.onChange();
    this.reportDrawEvent(offerBefore, stepped?.moved === false);
  }

  private reportDrawEvent(before: Color | null, sameMoves: boolean): void {
    const me = this.transport.localColor;
    if (me === null || before === this.drawOffer) return;
    const them = opposite(me);
    if (this.drawOffer === them) this.opts.onDrawEvent('received');
    else if (before === them) this.opts.onDrawEvent('withdrawn');
    // Our offer vanished with no move played and the game still on: it was turned down.
    else if (before === me && sameMoves && !this.gameState.isOver) this.opts.onDrawEvent('declined');
  }
}
